import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const MIN_PASSWORD_LENGTH = 12; // Matches server-side minimum in ADO-105626 U2 (security audit F-009).

function AcceptInvite() {
  const { token } = useParams();
  const { acceptInvite } = useAuth();

  // 'loading' | 'ready' | 'expired' | 'invalid' | 'already_used'
  const [state, setState] = useState('loading');
  const [invite, setInvite] = useState(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [errorCode, setErrorCode] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`/api/auth/validate-invite/${encodeURIComponent(token)}`)
      .then((response) => {
        if (cancelled) return;
        setInvite(response.data);
        setState('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 410) {
          setState('expired');
        } else if (status === 409) {
          setState('already_used');
        } else {
          setState('invalid');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorCode(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    const result = await acceptInvite(token, password);
    if (!result.success) {
      setError(result.error);
      setErrorCode(result.error_code);
      setSubmitting(false);
    }
    // success: AuthContext.setUser triggers <PublicRoute> redirect to /dashboard.
  };

  if (state === 'loading') {
    return <div className="loading">Loading...</div>;
  }

  if (state === 'expired') {
    return (
      <div className="container" style={{ maxWidth: '400px', marginTop: '100px' }}>
        <div className="card" style={{ padding: '30px' }}>
          <h2>This invitation has expired</h2>
          <p>Please contact the person who invited you to request a new link.</p>
        </div>
      </div>
    );
  }

  if (state === 'invalid' || state === 'already_used') {
    return (
      <div className="container" style={{ maxWidth: '400px', marginTop: '100px' }}>
        <div className="card" style={{ padding: '30px' }}>
          <h2>Invitation not found</h2>
          <p>This link is invalid or has already been used. Contact the person who invited you.</p>
        </div>
      </div>
    );
  }

  const greetingName = invite?.firstName || invite?.email || 'there';

  return (
    <div className="container" style={{ maxWidth: '400px', marginTop: '50px' }}>
      <div className="card" style={{ padding: '30px' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img
            src="https://www.point-of-rental.com/wp-content/uploads/2014/12/por-vert-e1449871819410.jpg"
            alt="Point of Rental"
            style={{ height: '60px', marginBottom: '20px' }}
          />
          <h2 style={{ margin: '0' }}>Welcome, {greetingName}</h2>
          <p style={{ color: '#666', fontSize: '14px', margin: '5px 0 0 0' }}>
            Set a password to activate your account
          </p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              name="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              className="form-control"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={submitting}
          >
            {submitting ? 'Activating...' : 'Activate Account'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AcceptInvite;
